import cytoscape from 'cytoscape';
import ELK from 'elkjs/lib/elk-api.js';
import { buildLayoutGeometry, geometryPositions, type GeometryOptions } from './elkLayoutGeometry';

// Below Fusion's 5s settlement guard, leaving time to apply the fallback and fit.
export const ELK_WORKER_DEADLINE_MS = 4000;
export const MONEY_FLOW_LAYOUT = {
  name: 'breadthfirst', directed: true, animate: false, fit: false,
  padding: 40, spacingFactor: 1.3, grid: false,
} as const;

type Options = GeometryOptions & {
  cy: cytoscape.Core;
  eles: cytoscape.Collection;
  fit?: boolean;
  padding?: number;
  zoom?: number;
  pan?: cytoscape.Position;
  ready?: cytoscape.EventHandler;
  stop?: cytoscape.EventHandler;
  transform?: (node: cytoscape.NodeSingular, position: cytoscape.Position) => cytoscape.Position;
};
interface WorkerLayout {
  options: Options;
  worker?: Worker;
  timer?: ReturnType<typeof setTimeout>;
  running: boolean;
  onDestroy: () => void;
  onRemove: () => void;
  onOtherLayout: (event: cytoscape.EventObject) => void;
  emit: (event: string) => void;
  one: (event: string, handler?: cytoscape.EventHandler) => void;
  run: () => WorkerLayout;
  stop: () => WorkerLayout;
}
const active = new WeakMap<cytoscape.Core, WorkerLayout>();

function cleanup(layout: WorkerLayout) {
  clearTimeout(layout.timer);
  layout.timer = undefined;
  if (layout.worker) {
    layout.worker.onmessage = null;
    layout.worker.onerror = null;
    layout.worker.onmessageerror = null;
    layout.worker.terminate();
    layout.worker = undefined;
  }
  const cy = layout.options.cy;
  cy.off('destroy', layout.onDestroy);
  cy.off('remove', layout.onRemove);
  cy.off('layoutstart', layout.onOtherLayout);
  if (active.get(cy) === layout) active.delete(cy);
}

function finish(layout: WorkerLayout, ready: boolean) {
  if (!layout.running) return;
  layout.running = false;
  cleanup(layout);
  if (ready) {
    layout.one('layoutready', layout.options.ready);
    layout.emit('layoutready');
  }
  layout.one('layoutstop', layout.options.stop);
  layout.emit('layoutstop');
}

function frame(layout: WorkerLayout) {
  const { cy, eles, fit, padding = 20, zoom, pan } = layout.options;
  if (fit) cy.fit(eles, padding);
  if (zoom != null) cy.zoom(zoom);
  if (pan) cy.pan(pan);
}

function fallback(layout: WorkerLayout) {
  if (!layout.running || layout.options.cy.destroyed()) return;
  cleanup(layout);
  const { cy, eles } = layout.options;
  const stable = eles.sort((a, b) => a.id() < b.id() ? -1 : a.id() > b.id() ? 1 : 0);
  const flow = stable.layout({
    ...MONEY_FLOW_LAYOUT,
    nodeDimensionsIncludeLabels: layout.options.nodeDimensionsIncludeLabels,
    depthSort: (a: cytoscape.NodeSingular, b: cytoscape.NodeSingular) => a.id() < b.id() ? -1 : a.id() > b.id() ? 1 : 0,
  } as cytoscape.LayoutOptions);
  // The fallback is an implementation of this layout, not another user layout.
  // Keep its internal events from bubbling; listeners receive this run's events once.
  flow.on('layoutstart layoutready layoutstop', (event) => event.stopPropagation());
  flow.run();
  frame(layout);
  finish(layout, true);
}

// Cytoscape invokes extension constructors with .call(), so this must be a function.
function ElkWorkerLayout(this: WorkerLayout, options: Options) {
  this.options = { fit: true, padding: 20, ...options };
  this.running = false;
  this.onDestroy = () => this.stop();
  this.onRemove = () => this.stop();
  this.onOtherLayout = (event) => { if (event.layout !== this) this.stop(); };
}

ElkWorkerLayout.prototype.run = function (this: WorkerLayout) {
  this.stop();
  const { cy, eles } = this.options;
  if (cy.destroyed()) return this;
  active.get(cy)?.stop();
  this.running = true;
  active.set(cy, this);
  cy.on('destroy', this.onDestroy);
  cy.on('remove', this.onRemove);
  cy.on('layoutstart', this.onOtherLayout);
  this.emit('layoutstart');
  if (!this.running || cy.destroyed()) return this;
  try {
    if (typeof Worker === 'undefined') { fallback(this); return this; }
    const { graph, leaves } = buildLayoutGeometry(eles, this.options);
    const worker = new Worker(new URL('./elkLayout.worker.ts', import.meta.url), { type: 'module', name: 'canvas-layout' });
    this.worker = worker;
    this.timer = setTimeout(() => fallback(this), ELK_WORKER_DEADLINE_MS);
    worker.onerror = (event) => { event.preventDefault(); fallback(this); };
    worker.onmessageerror = () => fallback(this);
    const elk = new ELK({ workerFactory: () => worker });
    void elk.layout(graph).then((result) => {
      if (!this.running || this.worker !== worker || cy.destroyed()) return;
      const positions = new Map(geometryPositions(result).map((position) => [position.id, position]));
      if ([...leaves.keys()].some((id) => {
        const p = positions.get(id);
        return !p || !Number.isFinite(p.x) || !Number.isFinite(p.y);
      })) { fallback(this); return; }
      cleanup(this);
      cy.batch(() => leaves.forEach((node, id) => {
        if (node.removed()) return;
        const { x, y } = positions.get(id)!;
        node.position(this.options.transform?.(node, { x, y }) ?? { x, y });
      }));
      frame(this);
      finish(this, true);
    }).catch(() => { if (this.running && this.worker === worker) fallback(this); });
  } catch {
    fallback(this);
  }
  return this;
};
ElkWorkerLayout.prototype.stop = function (this: WorkerLayout) { finish(this, false); return this; };
ElkWorkerLayout.prototype.destroy = ElkWorkerLayout.prototype.stop;

// Both canvases import this module. Neither imports cytoscape-elk, which would
// replace this registration with its synchronous engine and fake Worker.
cytoscape('layout', 'elk', ElkWorkerLayout);
